import axios from 'axios'
import { getAuthToken } from '@/lib/authToken'

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Attach the Clerk session token to every request.
api.interceptors.request.use(async (config) => {
  const token = await getAuthToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Bounce to sign-in on an expired/invalid session.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401 && window.location.pathname !== '/sign-in') {
      window.location.assign('/sign-in')
    }
    return Promise.reject(error)
  },
)

// Restaurants
export const getRestaurants = (params) => api.get('/restaurants', { params })
export const getRestaurant = (id) => api.get(`/restaurants/${id}`)

// Items
export const getItems = (restaurantId, params) => api.get(`/restaurants/${restaurantId}/items`, { params })
export const searchItems = (params) => api.get('/items', { params })
export const getCategories = (params) => api.get('/categories', { params })
export const getItem = (id) => api.get(`/items/${id}`)

// Generations
export const getGenerations = (itemId) => api.get(`/items/${itemId}/generations`)
export const toggleApproval = (genId, approved) => api.post(`/generations/${genId}/approve`, { approved })
export const toggleFlag = (genId, flagged) => api.post(`/generations/${genId}/flag`, { flagged })
export const getFlaggedResults = () => api.get('/flagged-results')

// Generation
export const getModels = () => api.get('/models')
export const estimateCost = (data) => api.post('/estimate-cost', data)
export const generateRestaurant = (data) => api.post('/generate/restaurant', data)
export const generateItem = (itemId, data) => api.post(`/generate/item/${itemId}`, data)
export const getJob = (jobId) => api.get(`/jobs/${jobId}`)
export const getJobs = (params) => api.get('/jobs', { params })

// Category-wide batch regeneration
export const generateCategory = (data) => api.post('/generate/category', data)
export const getJobItems = (jobId, params) => api.get(`/jobs/${jobId}/items`, { params })
export const getJobResults = (jobId) => api.get(`/jobs/${jobId}/results`)
export const getJobEvents = (jobId, params) => api.get(`/jobs/${jobId}/events`, { params })
export const resumeJob = (jobId) => api.post(`/jobs/${jobId}/resume`)
export const retryFailedJob = (jobId, mode) =>
  api.post(`/jobs/${jobId}/retry-failed`, null, { params: mode ? { mode } : {} })
export const cancelJob = (jobId) => api.post(`/jobs/${jobId}/cancel`)
export const pauseJob = (jobId) => api.post(`/jobs/${jobId}/pause`)
export const reviewJob = (jobId, approve) => api.post(`/jobs/${jobId}/review`, { approve })
export const getSetupStatus = () => api.get('/setup/status')
export const testPrompt = (data) => api.post('/test-prompt', data, { timeout: 180000 })

// Prompt templates
export const getPromptTemplates = () => api.get('/prompt-templates')
export const getPromptTemplate = (id) => api.get(`/prompt-templates/${id}`)
export const createPromptTemplate = (data) => api.post('/prompt-templates', data)
export const updatePromptTemplate = (id, data) => api.put(`/prompt-templates/${id}`, data)
export const deletePromptTemplate = (id) => api.delete(`/prompt-templates/${id}`)

// Export
export const exportApproved = () => api.post('/export/approved', {}, { responseType: 'blob' })

// Stats
export const getStats = () => api.get('/stats')

// Auth
export const getMe = () => api.get('/auth/me')

// Review cycle (reviewer)
export const getReviewSummary = (params) => api.get('/review/summary', { params })
export const getReviewBatch = (params) => api.get('/review/batch', { params })
export const decideReview = (batchId, data) => api.post(`/review/batch/${batchId}/decide`, data)
export const submitReview = (batchId) => api.post(`/review/batch/${batchId}/submit`)
export const abandonReview = (batchId) => api.post(`/review/batch/${batchId}/abandon`)
export const undoLastReview = () => api.post('/review/undo-last')
export const getFlagReasons = (params) => api.get('/review/flag-reasons', { params })

// Flagged queue (approver)
export const getFlagsSummary = () => api.get('/flags/summary')
export const getFlags = (params) => api.get('/flags', { params })
export const acceptFlags = (generationIds) => api.post('/flags/accept', { generation_ids: generationIds })
export const resetFlags = (generationIds) => api.post('/flags/reset', { generation_ids: generationIds })
export const regenerateFlags = (data) => api.post('/flags/regenerate', data)

export default api